import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function ScheduleAdmin() {
  const toast = useToast();
  const [data, setData] = useState({ announcements: [], count: 0 });
  const [form, setForm] = useState({ channel_id: "", message: "", scheduled_time: "", repeat_hours: 0, server_name: "" });
  const [advancedFor, setAdvancedFor] = useState(null);
  const [adv, setAdv] = useState({ warn_intervals: "", warn_messages: "", message2: "", message3: "", start_timing: "on_hour" });
  const load = () => api.get("/admin/schedule/list").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/schedule/create", {
        channel_id: parseInt(form.channel_id, 10),
        message: form.message,
        scheduled_time: form.scheduled_time,
        repeat_hours: parseInt(form.repeat_hours, 10) || 0,
        server_name: form.server_name || null,
      });
      toast.success("Scheduled");
      setForm({ channel_id: "", message: "", scheduled_time: "", repeat_hours: 0, server_name: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm(`Cancel scheduled announcement #${id}?`)) return;
    try { await api.delete(`/admin/schedule/${id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openAdvanced = (a) => {
    setAdvancedFor(a.id);
    setAdv({
      warn_intervals: a.warn_intervals || "",
      warn_messages: a.warn_messages || "",
      message2: a.message2 || "",
      message3: a.message3 || "",
      start_timing: a.start_timing || "on_hour",
    });
  };
  const saveAdvanced = async () => {
    try {
      await api.put("/admin/schedule/advanced", { id: advancedFor, ...adv });
      toast.success("Saved");
      setAdvancedFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4 fade-up" data-testid="schedule-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Scheduled Broadcasts</h3>
        <p className="text-xs text-[var(--muted)]">
          Schedules a Discord channel announcement (and optional in-game broadcast). The bot's scheduler picks these up from the shared <code>scheduled_announcements</code> table.
        </p>
      </div>
      <form onSubmit={create} className="glass-card p-5 space-y-3" data-testid="schedule-form">
        <h3 className="label">New Schedule</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Channel ID"><input className="input font-mono text-xs" required value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="schedule-channel" /></Field>
          <Field label="Scheduled Time (ISO 8601)"><input className="input font-mono text-xs" placeholder="2026-06-01T14:00:00" required value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} data-testid="schedule-time" /></Field>
          <Field label="Repeat every (hours, 0 = one-shot)"><input className="input" type="number" min="0" max="168" value={form.repeat_hours} onChange={(e) => setForm({ ...form, repeat_hours: e.target.value })} data-testid="schedule-repeat" /></Field>
          <Field label="In-game server (optional)"><input className="input" value={form.server_name} onChange={(e) => setForm({ ...form, server_name: e.target.value })} placeholder="leave blank = Discord only" data-testid="schedule-server" /></Field>
        </div>
        <Field label="Message"><textarea className="input" rows={3} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="schedule-message" /></Field>
        <button className="btn" data-testid="schedule-submit">Create</button>
      </form>
      <div className="glass-card p-5" data-testid="schedule-list">
        <h3 className="label mb-3">All ({data.count})</h3>
        <ul className="divide-y divide-white/5 max-h-96 overflow-y-auto">
          {data.announcements.map((a) => (
            <li key={a.id} className="py-3" data-testid={`schedule-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{a.message}</div>
                  <div className="text-[10px] text-[var(--muted)] mt-1">
                    #{a.id} · ch {a.channel_id} · {a.scheduled_time}
                    {a.repeat_hours ? ` · every ${a.repeat_hours}h` : " · one-shot"}
                    {a.server_name ? ` · server ${a.server_name}` : ""}
                    {!a.active && <span className="badge text-[var(--muted)] border-white/10 ml-1">inactive</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => openAdvanced(a)} className="btn btn-sm btn-secondary" data-testid={`schedule-advanced-${a.id}`}>Advanced</button>
                  {a.active === 1 && (
                    <button onClick={() => cancel(a.id)} className="btn btn-sm btn-danger" data-testid={`schedule-cancel-${a.id}`}>Cancel</button>
                  )}
                </div>
              </div>
              {advancedFor === a.id && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2 bg-black/30 p-3 rounded" data-testid={`schedule-advanced-form-${a.id}`}>
                  <Field label='warn_intervals (JSON e.g. "[300,60,30]")'><input className="input font-mono text-xs" value={adv.warn_intervals} onChange={(e) => setAdv({ ...adv, warn_intervals: e.target.value })} data-testid="adv-warn-intervals" /></Field>
                  <Field label='warn_messages (JSON list of strings)'><input className="input font-mono text-xs" value={adv.warn_messages} onChange={(e) => setAdv({ ...adv, warn_messages: e.target.value })} data-testid="adv-warn-messages" /></Field>
                  <Field label="message2"><input className="input" value={adv.message2} onChange={(e) => setAdv({ ...adv, message2: e.target.value })} data-testid="adv-message2" /></Field>
                  <Field label="message3"><input className="input" value={adv.message3} onChange={(e) => setAdv({ ...adv, message3: e.target.value })} data-testid="adv-message3" /></Field>
                  <Field label="start_timing">
                    <select className="input" value={adv.start_timing} onChange={(e) => setAdv({ ...adv, start_timing: e.target.value })} data-testid="adv-start-timing">
                      <option value="on_hour">on_hour</option>
                      <option value="on_half">on_half</option>
                      <option value="on_quarter">on_quarter</option>
                      <option value="exact">exact (use scheduled_time as-is)</option>
                    </select>
                  </Field>
                  <div className="flex gap-2 col-span-full">
                    <button onClick={saveAdvanced} className="btn btn-sm" data-testid="adv-save">Save Advanced</button>
                    <button onClick={() => setAdvancedFor(null)} className="btn btn-sm btn-secondary" data-testid="adv-cancel">Close</button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
